import { inject, Injectable } from '@angular/core';
import {
    calibrationDateFailed,
    calibrationDateRequested,
    calibrationDateSucceeded,
    CalibrationModel,
    cleanupCompletedPreviously,
    cleanupCompletedPreviouslyFailed,
    cleanupCompletedPreviouslySucceeded,
    CONFIRMATION_TYPES,
    confirmationCompletedPreviously,
    ConfirmationModel,
    confirmationOpened,
    confirmationSideEffectsFailed,
    confirmationSideEffectsSettled,
    getUuidV4,
    isDefined,
    MaterialService,
    NotificationIdentifierModel,
    NotificationModel,
    persistenceError,
    selectAddresses,
    selectConfirmationFor,
    selectConfirmationTimesheetContext,
    selectCurrentTimesheetMerged,
    selectUserCountry,
    signLater,
    slicePersisted,
    submitConfirmationAborted,
    submitConfirmationFailed,
    submitConfirmationRequested,
    submitConfirmationSucceeded,
    upsertConfirmation,
} from '@evorait/pace-core';
import { ParticipantCopyId, ParticipantParticulars } from '@evorait/pace-forms';
import { Action, Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { forkJoin, Observable, throwError } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { ConfirmationDataInterface } from '../interfaces/confirmation-data.interface';
import {
    calibrationFromOData,
    modelToConfirmationState,
    modelToMaterialsSnapshot,
    notificationIdToState,
    stateToConfirmationModel,
    summaryToSignatureSnapshot,
} from './confirmation-model.converters';

/**
 * NgRx-backed implementation of {@link ConfirmationDataInterface}. The confirmation collection
 * lives in the confirmation store slice (`apps/pace/src/app/confirmation/state/`); every read
 * uses a selector and every mutation dispatches an action. `ConfirmationSubmitEffects` and
 * `ConfirmationPersistenceEffects` own the orchestration and storage side effects — this service
 * only translates between the legacy `ConfirmationModel` and the slice's plain state types at the
 * public method boundary.
 *
 * This file — like the rest of the confirmation slice — lives in `apps/pace` rather than
 * `pace-core`; see the doc comment on {@link ConfirmationDataInterface} for why.
 */
@Injectable({
    providedIn: 'root',
})
export class ConfirmationNgrxService implements ConfirmationDataInterface {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    // The only non-store collaborator, and it is never called from here: it is handed straight to
    // `stateToConfirmationModel`, which needs it to merge the notification's locally stored
    // materials while translating a store entry into the legacy model.
    private readonly materialService = inject(MaterialService);

    public readonly confirmationCompletedPreviously$: Observable<void> = this.actions$.pipe(
        ofType(confirmationCompletedPreviously),
        map(() => undefined),
    );

    public getConfirmation(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): Observable<ConfirmationModel> {
        const notificationId = notificationIdToState(id);
        this.store.dispatch(confirmationOpened({ notificationId }));
        // `withLatestFrom` builds the parameterized selector once, when the pipe is constructed.
        // Reading the store inside the `map` instead would re-create the memoized selector — and a
        // signal — on every emission.
        // The merged projection, not the raw slice: the confirmation model reads the timesheet to show
        // the times other paused notifications occupy, and those are derived now rather than stored.
        return this.store.select(selectCurrentTimesheetMerged).pipe(
            filter(isDefined),
            take(1),
            withLatestFrom(
                this.store.select(selectConfirmationFor(notificationId, type)),
                this.store.select(selectAddresses),
                this.store.select(selectUserCountry),
            ),
            map(([timesheet, entry, addresses, userCountry]) =>
                stateToConfirmationModel(entry, id, type, timesheet, this.materialService, addresses, userCountry),
            ),
        );
    }

    /**
     * Completes once everything the save owes has landed — the slice's own write **and** the writes that
     * go elsewhere: the materials' local copy and the service report's recipients. That is the contract
     * the legacy implementation had, where the returned observable was the whole chain of storage write,
     * `saveMaterials` and the recipients write; settling on the slice alone would tell a caller the save
     * was done while two of the three were still in flight.
     *
     * The two settles are independent — `ConfirmationPersistenceEffects` writes the slice while
     * `ConfirmationSubmitEffects.saveConfirmationSideEffects$` does the rest — so both are awaited, and
     * either one failing fails the save. `forkJoin` subscribes in order, which is what makes this safe:
     * the side-effects listener is in place before {@link awaitOutcome} dispatches the request, so an
     * outcome that arrives synchronously cannot be missed.
     *
     * @param confirmation the confirmation to persist
     */
    public saveConfirmation(confirmation: ConfirmationModel): Observable<ConfirmationModel> {
        confirmation.saveTimestamp = Date.now();
        const correlationId = getUuidV4();
        return forkJoin([
            this.awaitSettle(correlationId, confirmationSideEffectsSettled.type, confirmationSideEffectsFailed.type),
            this.awaitOutcome(
                upsertConfirmation({
                    confirmation: modelToConfirmationState(confirmation, confirmation.saveTimestamp),
                    materials: modelToMaterialsSnapshot(confirmation),
                    correlationId,
                }),
                correlationId,
                slicePersisted.type,
                persistenceError.type,
            ),
        ]).pipe(map(() => confirmation));
    }

    /**
     * Completes without emitting when the confirmation turns out to have been completed already —
     * the legacy method gated its pipeline on `filterConfirmationStatusComplete()`, an rxjs `filter`,
     * so callers observe completion-without-emission rather than a value or an error. Preserved via
     * `submitConfirmationAborted`, since `ConfirmationSummaryComponent.submit()` relies on it: its
     * downstream `mergeMap`s (navigate to dashboard, sync) must not run, but its `catchError` must
     * not fire either.
     *
     * @param confirmation
     */
    public submitConfirmation(confirmation: ConfirmationModel): Observable<void> {
        const correlationId = getUuidV4();
        const snapshot = modelToConfirmationState(confirmation, Date.now());
        // What was signed travels beside the confirmation, never in it: the slice stores the confirmation,
        // and the signatures must not reach storage.
        const signatureSnapshot = summaryToSignatureSnapshot(confirmation.summary);
        // The timesheet slice reduces the same action to commit the confirmation's work entries, and a
        // reducer cannot reach across slices for the customer, address and config switches that
        // derivation needs — so they are read here, once, and carried on the action.
        const timesheet = this.store.selectSignal(
            selectConfirmationTimesheetContext(notificationIdToState(confirmation.notificationIdentifier)),
        )();
        return this.awaitOutcome(
            submitConfirmationRequested({
                confirmation: snapshot,
                signatureSnapshot,
                materials: modelToMaterialsSnapshot(confirmation),
                timesheet,
                correlationId,
            }),
            correlationId,
            submitConfirmationSucceeded.type,
            submitConfirmationFailed.type,
            submitConfirmationAborted.type,
        ).pipe(map(() => undefined));
    }

    public confirmationCompletedPreviouslyCleanup(confirmation: ConfirmationModel): Observable<boolean> {
        const notificationId = notificationIdToState(confirmation.notificationIdentifier);
        const correlationId = getUuidV4();
        return this.awaitOutcome(
            cleanupCompletedPreviously({ notificationId, confirmationType: confirmation.type, correlationId }),
            correlationId,
            cleanupCompletedPreviouslySucceeded.type,
            cleanupCompletedPreviouslyFailed.type,
        ).pipe(map(() => true));
    }

    public triggerConfirmationCompletedPreviously(): void {
        this.store.dispatch(confirmationCompletedPreviously());
    }

    public signLater(notification: NotificationModel): void {
        this.store.dispatch(signLater({ notificationId: notificationIdToState(notification.identifier) }));
    }

    public getCalibrationDate(measurementToolId: string): Observable<CalibrationModel> {
        const correlationId = getUuidV4();
        return this.awaitOutcome<ReturnType<typeof calibrationDateSucceeded>>(
            calibrationDateRequested({ measurementToolId, correlationId }),
            correlationId,
            calibrationDateSucceeded.type,
            calibrationDateFailed.type,
        ).pipe(map(({ calibration }) => calibrationFromOData(calibration)));
    }

    // this function is being called when the user is removing a participant from pace forms.
    // We need to remove the corresponding attendee from the attendee list for the participant
    public removeAttendee(): Observable<ParticipantCopyId> {
        return throwError(() => `ConfirmationNgrxService: removeAttendee: Method should be called only from PACE US`);
    }

    public saveAttendee(): Observable<void> {
        return throwError(() => `ConfirmationNgrxService: saveAttendee: Method should be called only from PACE US`);
    }

    // Only used in PACE US
    public getParticipantsFromAttendees(): Observable<ParticipantParticulars[]> {
        return throwError(
            () => `ConfirmationNgrxService: getParticipantsFromAttendees: Method should be called only from PACE US`,
        );
    }

    /**
     * Dispatches `request` and completes once the matching `succeededType`/`failedType` outcome
     * arrives, filtered by `correlationId`. The outcome subscription is set up before dispatching
     * so a synchronous result cannot be missed — the same pattern as
     * `TimesheetAndExpensesNgrxService.dispatchSaveRequest`. This is the one private helper this
     * service needs beyond the two kinds listed in `architecture-rules.md` (get-or-create,
     * guarded-dispatch): `submitConfirmation` and `confirmationCompletedPreviouslyCleanup` both
     * must return an `Observable` that only resolves once an async effect pipeline completes, and
     * this dispatch-and-await-outcome shape is the established way ngrx facades expose that in
     * this codebase (see also `EscalationNgrxService`, `BarcodeScannerNgrxService`).
     *
     * Emits the succeeded action itself, so query-style callers can translate its payload into the
     * legacy model their contract returns; callers that only need completion map it away.
     *
     * @param request the action to dispatch
     * @param correlationId the id carried by the request and echoed by every outcome action
     * @param succeededType the outcome action type that resolves the observable
     * @param failedType the outcome action type (carrying an `error` field) that errors it
     * @param abortedType optional outcome type that completes without emitting, for callers that
     * distinguish "nothing to do" from both success and failure (see {@link submitConfirmation}
     * and {@link filterConfirmationStatusComplete})
     */
    private awaitOutcome<T extends Action = Action>(
        request: Action,
        correlationId: string,
        succeededType: string,
        failedType: string,
        abortedType?: string,
    ): Observable<T> {
        return new Observable<T>((subscriber) => {
            const subscription = this.awaitSettle<T>(correlationId, succeededType, failedType, abortedType).subscribe(
                subscriber,
            );
            this.store.dispatch(request);
            return () => subscription.unsubscribe();
        });
    }

    /**
     * The listening half of {@link awaitOutcome}: settles on the matching outcome without dispatching
     * anything itself.
     *
     * Split out for {@link saveConfirmation}, which must wait for two independent settles from a single
     * dispatch. Subscribing to this one first and to `awaitOutcome` second is what makes that safe — the
     * second listener exists before the request goes out, so an outcome cannot be missed.
     *
     * @param correlationId the id every outcome for this request echoes
     * @param succeededType the outcome action type that resolves the observable
     * @param failedType the outcome action type (carrying an `error` field) that errors it
     * @param abortedType optional outcome type that completes without emitting
     */
    private awaitSettle<T extends Action = Action>(
        correlationId: string,
        succeededType: string,
        failedType: string,
        abortedType?: string,
    ): Observable<T> {
        return this.actions$.pipe(
            filter(
                (action: Action & { correlationId?: string }) =>
                    (action.type === succeededType || action.type === failedType || action.type === abortedType) &&
                    action.correlationId === correlationId,
            ),
            take(1),
            map((action) => {
                if (action.type === failedType) {
                    throw new Error((action as Action & { error: string }).error);
                }
                return action as T;
            }),
            filter((action) => action.type !== abortedType),
        );
    }
}
